import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useJobRuns } from '../../hooks/useAdmin';
import { formatDate, JOB_LABELS, sortRows, toggleDir, jobStatsSummary } from './adminUtils';

export default function AdminJobRuns() {
    const { data: jobRuns = [], isLoading } = useJobRuns();
    const [sort, setSort] = useState({ key: 'timestamp', dir: 'desc' });
    const [expandedJobs, setExpandedJobs] = useState({});
    const toggleJob = (id) => setExpandedJobs(prev => ({ ...prev, [id]: !prev[id] }));

    const sorted = sortRows(jobRuns, sort.key, sort.dir, (r, k) => {
        if (k === 'timestamp') return r.timestamp;
        if (k === 'action_type') return JOB_LABELS[r.action_type] ?? r.action_type;
        if (k === 'stats') return jobStatsSummary(r.action_type, r.params);
        return '';
    });

    const sp = (key) => ({
        active: sort.key === key,
        direction: sort.key === key ? sort.dir : 'asc',
        onClick: () => setSort(prev => toggleDir(prev, key)),
    });

    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
                Izvršavanja poslova (zadnjih 50)
            </Typography>
            <Paper sx={{ overflow: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 600 }}><TableSortLabel {...sp('timestamp')}>Vrijeme</TableSortLabel></TableCell>
                            <TableCell sx={{ fontWeight: 600 }}><TableSortLabel {...sp('action_type')}>Posao</TableSortLabel></TableCell>
                            <TableCell sx={{ fontWeight: 600 }}><TableSortLabel {...sp('stats')}>Statistike</TableSortLabel></TableCell>
                            <TableCell sx={{ width: 40 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sorted.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ color: '#999', py: 3 }}>
                                    Nema evidentiranih izvršavanja.
                                </TableCell>
                            </TableRow>
                        )}
                        {sorted.map((run) => {
                            const hasLog = !!run.params?.log;
                            return (
                                <React.Fragment key={run.id}>
                                    <TableRow
                                        hover
                                        onClick={hasLog ? () => toggleJob(run.id) : undefined}
                                        sx={hasLog ? { cursor: 'pointer' } : {}}
                                    >
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>{formatDate(run.timestamp)}</TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                                                    backgroundColor:
                                                        run.action_type === 'scraper_run' ? '#e3f2fd' :
                                                        run.action_type === 'digest_run' ? '#e8f5e9' :
                                                        run.action_type === 'scheduled_search_run' ? '#fff3e0' :
                                                        run.action_type === 'manual_trigger' ? '#fce4ec' : '#f3e5f5',
                                                }}
                                            >
                                                {JOB_LABELS[run.action_type] ?? run.action_type}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontSize: 13, color: '#555' }}>{jobStatsSummary(run.action_type, run.params)}</TableCell>
                                        <TableCell align="right" sx={{ p: 0.5 }}>
                                            {hasLog && (
                                                <IconButton size="small">
                                                    <ExpandMoreIcon fontSize="small" sx={{ transform: expandedJobs[run.id] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                    {hasLog && (
                                        <TableRow>
                                            <TableCell colSpan={4} sx={{ py: 0, border: expandedJobs[run.id] ? undefined : 0 }}>
                                                <Collapse in={!!expandedJobs[run.id]} unmountOnExit>
                                                    {run.action_type === 'scraper_run' && run.params?.portal_stats && Object.keys(run.params.portal_stats).length > 0 && (
                                                        <Table size="small" sx={{ m: 1, mb: 0, width: 'auto' }}>
                                                            <TableHead>
                                                                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                                                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Portal</TableCell>
                                                                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }} align="right">Skenirano</TableCell>
                                                                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }} align="right">Novo</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {Object.entries(run.params.portal_stats)
                                                                    .sort(([a], [b]) => a.localeCompare(b))
                                                                    .map(([portal, stats]) => (
                                                                        <TableRow key={portal}>
                                                                            <TableCell sx={{ fontSize: 12 }}>{portal}</TableCell>
                                                                            <TableCell sx={{ fontSize: 12 }} align="right">{stats.scanned}</TableCell>
                                                                            <TableCell sx={{ fontSize: 12, fontWeight: stats.new > 0 ? 600 : 400, color: stats.new > 0 ? '#27ae60' : 'inherit' }} align="right">{stats.new}</TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                            </TableBody>
                                                        </Table>
                                                    )}
                                                    <Box component="pre" sx={{ fontSize: 12, backgroundColor: '#1e1e1e', color: '#f8f8f2', p: 2, borderRadius: 1, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', m: 1 }}>
                                                        {run.params.log}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
}
